import { supabase } from '../lib/supabase';

// Real-time subscription for student goals and related data
export class RealtimeSubscriptionService {
  constructor() {
    this.channels = new Map();
  }

  // Subscribe to student goals changes
  subscribeToGoals(userId, onUpdate) {
    const channelName = `student-goals-${userId}`;
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'student_goals',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          console.log('Goal change detected:', payload);
          onUpdate(payload);
        }
      )?.subscribe((status) => {
        console.log('Goals subscription status:', status);
      });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Subscribe to achievement badges changes
  subscribeToAchievements(userId, onUpdate) {
    const channelName = `achievement-badges-${userId}`;
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'achievement_badges',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          console.log('Achievement badge change detected:', payload);
          onUpdate(payload);
        }
      )?.subscribe((status) => {
        console.log('Achievements subscription status:', status);
      });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Subscribe to practice streak changes
  subscribeToStreak(userId, onUpdate) {
    const channelName = `practice-streaks-${userId}`;
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'practice_streaks',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          console.log('Streak change detected:', payload);
          onUpdate(payload);
        }
      )?.subscribe((status) => {
        console.log('Streak subscription status:', status);
      });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Subscribe to leaderboard changes (pronunciation analytics)
  subscribeToLeaderboard(onUpdate) {
    const channelName = 'pronunciation-leaderboard';
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pronunciation_analytics'
        },
        (payload) => {
          console.log('Leaderboard change detected:', payload);
          onUpdate(payload);
        }
      )?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_profiles'
        },
        (payload) => {
          console.log('User profile change detected:', payload);
          onUpdate(payload);
        }
      )?.subscribe((status) => {
        console.log('Leaderboard subscription status:', status);
      });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Subscribe to recording sessions for live status updates
  subscribeToRecordingSessions(onUpdate) {
    const channelName = 'recording-sessions';
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'recording_sessions'
        },
        (payload) => {
          console.log('Recording session change detected:', payload);
          onUpdate(payload);
        }
      )?.subscribe((status) => {
        console.log('Recording sessions subscription status:', status);
      });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Subscribe to voice actor assignments for live updates
  subscribeToAssignments(onUpdate) {
    const channelName = 'voice-actor-assignments';
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'voice_actor_assignments'
        },
        (payload) => {
          console.log('Voice actor assignment change detected:', payload);
          onUpdate(payload);
        }
      )?.subscribe((status) => {
        console.log('Assignments subscription status:', status);
      });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Subscribe to voice actor assignments for specific voice actor
  subscribeToVoiceActorAssignments(voiceActorId, onUpdate) {
    const channelName = `voice-actor-assignments-${voiceActorId}`;
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'voice_actor_assignments',
          filter: `voice_actor_id=eq.${voiceActorId}`
        },
        (payload) => {
          console.log('Voice actor assignment change detected:', payload);
          onUpdate(payload);
        }
      )?.subscribe((status) => {
        console.log('Voice actor assignments subscription status:', status);
      });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Subscribe to voice actor availability changes
  subscribeToVoiceActorAvailability(onUpdate) {
    const channelName = 'voice-actor-availability';
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'voice_actor_availability'
        },
        (payload) => {
          console.log('Voice actor availability change detected:', payload);
          onUpdate(payload);
        }
      )?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'voice_actors',
          filter: 'is_available=eq.true'
        },
        (payload) => {
          console.log('Voice actor status change detected:', payload);
          onUpdate(payload);
        }
      )?.subscribe((status) => {
        console.log('Voice actor availability subscription status:', status);
      });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Subscribe to voice actors changes (for Talented Performers section)
  subscribeToVoiceActors(onUpdate) {
    const channelName = 'voice-actors-realtime';
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'voice_actors'
        },
        (payload) => {
          console.log('Voice actor change detected:', payload);
          onUpdate(payload);
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_profiles',
          filter: 'is_voice_actor=eq.true'
        },
        (payload) => {
          console.log('User profile voice actor status change detected:', payload);
          onUpdate(payload);
        }
      )
      ?.subscribe((status) => {
        console.log('Voice actors subscription status:', status);
      });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Subscribe to audio recordings for recording progress tracking
  subscribeToAudioRecordings(playId, onUpdate) {
    const channelName = playId ? `audio-recordings-${playId}` : 'audio-recordings-all';
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channelConfig = supabase?.channel(channelName);
    
    const channel = playId
      ? channelConfig?.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'audio_recordings'
          },
          (payload) => {
            console.log('Audio recording change detected:', payload);
            onUpdate(payload);
          }
        )?.subscribe((status) => {
          console.log('Audio recordings subscription status:', status);
        })
      : channelConfig?.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'audio_recordings'
          },
          (payload) => {
            console.log('Audio recording change detected:', payload);
            onUpdate(payload);
          }
        )?.subscribe((status) => {
          console.log('Audio recordings subscription status:', status);
        });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Subscribe to user achievements (unlocked achievements)
  subscribeToUserAchievements(onUpdate, userId = null) {
    const channelName = userId ? `user-achievements-${userId}` : 'user-achievements-all';
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channelConfig = supabase?.channel(channelName);
    
    const channel = userId
      ? channelConfig?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'user_achievements',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            console.log('User achievement unlocked:', payload);
            onUpdate({
              type: 'achievement_unlocked',
              data: payload?.new,
              timestamp: new Date()
            });
          }
        )?.subscribe((status) => {
          console.log('User achievements subscription status:', status);
        })
      : channelConfig?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'user_achievements'
          },
          (payload) => {
            console.log('User achievement unlocked (all users):', payload);
            onUpdate({
              type: 'achievement_unlocked',
              data: payload?.new,
              timestamp: new Date()
            });
          }
        )?.subscribe((status) => {
          console.log('User achievements (all) subscription status:', status);
        });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Subscribe to achievement badges earned
  subscribeToAchievementBadges(onUpdate, userId = null) {
    const channelName = userId ? `badges-${userId}` : 'badges-all';
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channelConfig = supabase?.channel(channelName);
    
    const channel = userId
      ? channelConfig?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'achievement_badges',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            console.log('Badge earned:', payload);
            onUpdate({
              type: 'badge_earned',
              data: payload?.new,
              timestamp: new Date()
            });
          }
        )?.subscribe((status) => {
          console.log('Badge subscription status:', status);
        })
      : channelConfig?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'achievement_badges'
          },
          (payload) => {
            console.log('Badge earned (all users):', payload);
            onUpdate({
              type: 'badge_earned',
              data: payload?.new,
              timestamp: new Date()
            });
          }
        )?.subscribe((status) => {
          console.log('Badge (all) subscription status:', status);
        });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Subscribe to quiz completions
  subscribeToQuizCompletions(onUpdate, userId = null) {
    const channelName = userId ? `quiz-completions-${userId}` : 'quiz-completions-all';
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channelConfig = supabase?.channel(channelName);
    
    const channel = userId
      ? channelConfig?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'quiz_attempts',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            console.log('Quiz completed:', payload);
            onUpdate({
              type: 'quiz_completed',
              data: payload?.new,
              timestamp: new Date()
            });
          }
        )?.subscribe((status) => {
          console.log('Quiz completions subscription status:', status);
        })
      : channelConfig?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'quiz_attempts'
          },
          (payload) => {
            console.log('Quiz completed (all users):', payload);
            onUpdate({
              type: 'quiz_completed',
              data: payload?.new,
              timestamp: new Date()
            });
          }
        )?.subscribe((status) => {
          console.log('Quiz completions (all) subscription status:', status);
        });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Subscribe to pronunciation analytics for performance feedback
  subscribeToPronunciationAnalytics(userId, onUpdate) {
    const channelName = `pronunciation-analytics-${userId}`;
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pronunciation_analytics',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          console.log('Pronunciation analytics change detected:', payload);
          onUpdate(payload);
        }
      )?.subscribe((status) => {
        console.log('Pronunciation analytics subscription status:', status);
      });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Subscribe to recording sessions for specific voice actor
  subscribeToVoiceActorRecordingSessions(voiceActorId, onUpdate) {
    const channelName = `recording-sessions-${voiceActorId}`;
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'recording_sessions',
          filter: `voice_actor_id=eq.${voiceActorId}`
        },
        (payload) => {
          console.log('Recording session change detected:', payload);
          onUpdate(payload);
        }
      )?.subscribe((status) => {
        console.log('Recording sessions subscription status:', status);
      });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Combined subscription for all student activity (for admin/educator dashboards)
  subscribeToStudentActivity(onUpdate) {
    const channelName = 'student-activity-combined';
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_achievements'
        },
        (payload) => {
          console.log('Achievement unlocked (activity feed):', payload);
          onUpdate({
            type: 'achievement_unlocked',
            data: payload?.new,
            timestamp: new Date()
          });
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'achievement_badges'
        },
        (payload) => {
          console.log('Badge earned (activity feed):', payload);
          onUpdate({
            type: 'badge_earned',
            data: payload?.new,
            timestamp: new Date()
          });
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'quiz_attempts'
        },
        (payload) => {
          console.log('Quiz completed (activity feed):', payload);
          onUpdate({
            type: 'quiz_completed',
            data: payload?.new,
            timestamp: new Date()
          });
        }
      )
      ?.subscribe((status) => {
        console.log('Student activity subscription status:', status);
      });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Broadcast session status update to all connected clients
  async broadcastSessionStatus(sessionId, status, metadata = {}) {
    const channel = supabase?.channel('session-status-broadcast');
    
    await channel?.send({
      type: 'broadcast',
      event: 'session-status-update',
      payload: {
        sessionId,
        status,
        timestamp: new Date()?.toISOString(),
        ...metadata
      }
    });
  }

  // Subscribe to session status broadcasts
  subscribeToSessionStatusBroadcast(onUpdate) {
    const channelName = 'session-status-broadcast';
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'broadcast',
        { event: 'session-status-update' },
        (payload) => {
          console.log('Session status broadcast received:', payload);
          onUpdate(payload);
        }
      )?.subscribe((status) => {
        console.log('Session status broadcast subscription status:', status);
      });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Broadcast recording progress update
  async broadcastRecordingProgress(lineGroupId, progress, metadata = {}) {
    const channel = supabase?.channel('recording-progress-broadcast');
    
    await channel?.send({
      type: 'broadcast',
      event: 'recording-progress-update',
      payload: {
        lineGroupId,
        progress,
        timestamp: new Date()?.toISOString(),
        ...metadata
      }
    });
  }

  // Subscribe to recording progress broadcasts
  subscribeToRecordingProgressBroadcast(onUpdate) {
    const channelName = 'recording-progress-broadcast';
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'broadcast',
        { event: 'recording-progress-update' },
        (payload) => {
          console.log('Recording progress broadcast received:', payload);
          onUpdate(payload);
        }
      )?.subscribe((status) => {
        console.log('Recording progress broadcast subscription status:', status);
      });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Broadcast voice actor availability change
  async broadcastAvailabilityChange(voiceActorId, isAvailable, metadata = {}) {
    const channel = supabase?.channel('availability-broadcast');
    
    await channel?.send({
      type: 'broadcast',
      event: 'availability-change',
      payload: {
        voiceActorId,
        isAvailable,
        timestamp: new Date()?.toISOString(),
        ...metadata
      }
    });
  }

  // Subscribe to availability change broadcasts
  subscribeToAvailabilityBroadcast(onUpdate) {
    const channelName = 'availability-broadcast';
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'broadcast',
        { event: 'availability-change' },
        (payload) => {
          console.log('Availability change broadcast received:', payload);
          onUpdate(payload);
        }
      )?.subscribe((status) => {
        console.log('Availability broadcast subscription status:', status);
      });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Subscribe to goal milestones
  subscribeToMilestones(goalIds, onUpdate) {
    const channelName = `goal-milestones-${goalIds?.join('-')}`;
    
    if (this.channels?.has(channelName)) {
      return () => this.unsubscribe(channelName);
    }

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'goal_milestones',
          filter: `goal_id=in.(${goalIds?.join(',')})`
        },
        (payload) => {
          console.log('Milestone change detected:', payload);
          onUpdate(payload);
        }
      )?.subscribe((status) => {
        console.log('Milestones subscription status:', status);
      });

    this.channels?.set(channelName, channel);

    return () => this.unsubscribe(channelName);
  }

  // Unsubscribe from a specific channel
  unsubscribe(channelName) {
    const channel = this.channels?.get(channelName);
    if (channel) {
      supabase?.removeChannel(channel);
      this.channels?.delete(channelName);
      console.log(`Unsubscribed from ${channelName}`);
    }
  }

  // Unsubscribe from all channels
  unsubscribeAll() {
    this.channels?.forEach((channel, channelName) => {
      supabase?.removeChannel(channel);
      console.log(`Unsubscribed from ${channelName}`);
    });
    this.channels?.clear();
  }
}

// Export singleton instance
export const realtimeService = new RealtimeSubscriptionService();